import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import classNames from 'classnames'
import moment from 'moment-timezone'

import useRequestPending from 'shared/hooks/useRequestPending'
import CircleLoader from 'shared/components/CircleLoader'
import ErrorMessage from 'shared/components/ErrorMessage'
import Pin from 'shared/components/Pin'
import { isDesktop } from 'shared/responsive-helpers'
import { MarkerStyle } from 'shared/generate-marker-style'
import { useConfirm } from 'shared/hooks/useConfirm'
import featureToggles from 'shared/featureToggles'
import scrollTop from 'shared/scroll-top'
import { getAnalyticsFlowType, sendAmplitudeEvent, useSendGTAGEvent } from 'analytics/utils'

import withRates from 'shared/hocs/withRates'
import { Map } from 'booking/components/Map'
import { ParkingInfoSideBar } from 'booking/components/ParkingInfoSideBar'
import {
    clearSelectedRate,
    selectSeasonTicket,
    setLastBookingDetails,
    removePromoCode,
    setRawPromoCode,
    setCurrentCustomerFlow,
} from 'booking/actions'
import { clearGuestReservationsState } from 'manage-guest-user-booking/actions'

import { rateSelector, waitingListKeySelector, selectedTicketRatePendingSelector } from 'booking/selectors'

import {
    BOOKING_FLOW_HEADER_PREFIX,
    BOOKING_FLOW,
    EVENT_FLOW,
    SHOW_HOURLY_FLOW_PARK_NOW,
    BOOKING_FLOW_NON_BOOKABLE,
    SHOW_HOURLY_FLOW,
} from 'booking/constants'
import { SEARCH_MODE_EVENTS } from 'search/constants'
import { setDateTimeRange } from 'search-date-time'
import { cancellationSelector } from 'dashboard-transactions/selectors'
import { getTimeSpan } from 'shared/time-utilites'

import PurchaseFlowHeader from 'subscription-flow/components/PurchaseFlowHeader/PurchaseFlowHeader'
import SuccessPage from 'subscription-flow/components/SuccessPage'

import {
    FLOW_STEP_DATE,
    FLOW_STEP_PRODUCT,
    FLOW_STEP_SUCCESS,
    FLOW_STEP_VEHICLE,
    FLOW_STEP_EVENT,
} from 'subscription-flow/constants'
import { credentialTypeSelector } from 'subscription-flow/canada/selectors'

import { HourlyFlow } from 'booking/components/HourlyFlow'
import { EventFlow } from 'booking/components/EventFlow'

import styles from 'subscription-flow/styles.module.scss'
import bookingStyles from 'booking/components/flow-steps/styles.module.scss'

const initialState = {
    startDate: null,
    product: null,
    event: null,
    vehicles: null,
    details: null,
}

const $BookingSelection = ({
    detailsPending,
    errorMessage,
    ticketsPending,
    rate,
    clearSelectedRate,
    grsId,
    locationDetails,
    setLastBookingDetails,
    waitingListKey,
    rateVehicle,
    removePromoCode,
    setRawPromoCode,
    parkingMode,
    defaultCustomerFlow,
    clearGuestReservationsState,
    reservationAmendPolicy,
    currentCustomerFlow,
    setCurrentCustomerFlow,
    showOnlyBookInAdvanceFlow,
    getRates,
    setDateTimeRange,
}) => {
    const { confirm, ConfirmModal } = useConfirm()
    const location = useLocation()
    const { isLoading: ticketsLoading } = useRequestPending(ticketsPending)
    const { isLoading: locationLoading } = useRequestPending(detailsPending)
    const sendGTAGEvent = useSendGTAGEvent()
    const [currentItemIndex] = React.useState(1)
    const isHourlyFlow = SHOW_HOURLY_FLOW.includes(currentCustomerFlow)
    const isParkNowFlow = currentCustomerFlow === SHOW_HOURLY_FLOW_PARK_NOW
    const nonBookable = !locationDetails.reservationsEnabled
    const activeStepsFlow = isHourlyFlow ? (nonBookable ? BOOKING_FLOW_NON_BOOKABLE : BOOKING_FLOW) : EVENT_FLOW
    const showPixPaymentMethod = featureToggles.hasPIXPurchase && (isHourlyFlow || parkingMode === SEARCH_MODE_EVENTS)
    const showContent = !(locationLoading || ticketsLoading) && !errorMessage
    const analyticsFlowType = getAnalyticsFlowType(parkingMode, currentCustomerFlow)

    const addGuestCheckoutFlowClassName = () => locationDetails.guestCheckoutEnabled && activeStep === FLOW_STEP_VEHICLE

    // The step is stored together with the flow it was chosen in. Switching flow renders the
    // other flow component immediately, while the step is only reset in an effect below.
    const [step, setStep] = useState({ parkingMode, currentCustomerFlow, value: null })
    const activeStep =
        step.parkingMode === parkingMode && step.currentCustomerFlow === currentCustomerFlow ? step.value : null
    const setActiveStep = value => setStep({ parkingMode, currentCustomerFlow, value })

    const [bookingData, setBookingData] = useState(initialState)
    const { geoLocation, name } = locationDetails

    useEffect(() => {
        const defaultTimeRange = getTimeSpan(moment(), isParkNowFlow ? 0 : 30, 120)
        setDateTimeRange(defaultTimeRange.start, defaultTimeRange.end)
        if (currentCustomerFlow && isHourlyFlow) {
            getRates({ grsId, waitingListKey })
        }
    }, [currentCustomerFlow])

    useEffect(() => {
        setActiveStep(isHourlyFlow ? FLOW_STEP_DATE : FLOW_STEP_EVENT)
        setBookingData(initialState)
    }, [parkingMode, currentCustomerFlow])

    useEffect(() => {
        clearSelectedRate()
    }, [])

    useEffect(() => {
        if (locationDetails?.name && analyticsFlowType) {
            sendAmplitudeEvent('Begin Checkout', {
                'Location Name': locationDetails?.name,
                'Flow Type': analyticsFlowType,
            })
            sendGTAGEvent('begin_checkout', {
                location_name: locationDetails?.name,
                flow_type: analyticsFlowType,
            })
        }
    }, [locationDetails, analyticsFlowType])

    useEffect(() => {
        setLastBookingDetails({
            id: currentItemIndex,
            grsId,
            rate,
            waitingListKey,
        })
    }, [grsId, rate, waitingListKey])

    useEffect(() => {
        scrollTop()
    }, [activeStep])

    useEffect(
        () => () => {
            clearGuestReservationsState()
        },
        [location?.pathname]
    )

    const onNext = () => {
        const newIndex = activeStepsFlow.indexOf(activeStep) + 1

        if (newIndex < activeStepsFlow.length) {
            setActiveStep(activeStepsFlow[newIndex])
        }
    }

    const setStartDate = startDate => {
        setBookingData({ ...bookingData, startDate })
        sendAmplitudeEvent('Selected Date', {
            'Flow Type': analyticsFlowType,
        })

        sendGTAGEvent('select_date', {
            flow_type: analyticsFlowType,
        })
    }

    const setProduct = product => {
        setBookingData({ ...bookingData, product })
    }

    const setVehicle = vehicles => {
        setBookingData({ ...bookingData, vehicles })
    }

    const setEvent = event => {
        setBookingData({ ...bookingData, event })
    }

    const setDateRange = date => {
        setBookingData({ ...bookingData, date })
    }

    const setOutboundNumber = data => {
        setBookingData({ ...bookingData, outboundNumber: data.outboundNumber })
    }

    const setInboundNumber = data => {
        setBookingData({ ...bookingData, inboundNumber: data.inboundNumber })
    }

    const setAdditionalServices = data => {
        setBookingData({ ...bookingData, additionalServices: data })
    }

    const onStepClick = async step => {
        const { startDate } = bookingData
        let { date, product, delivery, vehicles, event, outboundNumber, inboundNumber, additionalServices } =
            bookingData

        if (step === FLOW_STEP_PRODUCT) {
            product = null
            vehicles = null
            delivery = null
            outboundNumber = null
            inboundNumber = null
            additionalServices = null
            clearSelectedRate()
        }

        if (step === FLOW_STEP_VEHICLE) {
            vehicles = null
            delivery = null
            outboundNumber = null
            inboundNumber = null
        }

        if (step === FLOW_STEP_DATE) {
            const isConfirmed = await confirm()
            if (!isConfirmed) {
                return
            }
            product = null
            vehicles = null
            delivery = null
            outboundNumber = null
            inboundNumber = null
            additionalServices = null
        }

        if (step === FLOW_STEP_EVENT) {
            const isConfirmed = await confirm()
            if (!isConfirmed) {
                return
            }
            date = null
            event = null
            vehicles = null
        }

        setRawPromoCode('')
        removePromoCode()
        setActiveStep(step)
        setBookingData({
            ...bookingData,
            startDate,
            product,
            delivery,
            vehicles,
            date,
            event,
            outboundNumber,
            inboundNumber,
            additionalServices,
        })
    }

    if (!bookingData.inboundNumber && reservationAmendPolicy?.inboundFlightNumber) {
        setInboundNumber({ inboundNumber: reservationAmendPolicy.inboundFlightNumber })
    }

    if (!bookingData.outboundNumber && reservationAmendPolicy?.outboundFlightNumber) {
        setOutboundNumber({ outboundNumber: reservationAmendPolicy.outboundFlightNumber })
    }

    return (
        <div
            className={classNames(
                styles.purchaseContainer,
                addGuestCheckoutFlowClassName() && styles.guestCheckoutVehicleContainer
            )}>
            <ConfirmModal
                title="subscription.purchase.step.confirm.title"
                text="subscription.purchase.step.confirm.text"
            />
            <div className={styles.purchaseScreen}>
                {activeStep === FLOW_STEP_SUCCESS ? (
                    <SuccessPage href="/dashboard/transactions" />
                ) : (
                    <div className={classNames(styles.flowContainer)}>
                        {(locationLoading || ticketsLoading) && <CircleLoader />}
                        {errorMessage && <ErrorMessage messages={[errorMessage]} />}
                        {showContent && (
                            <div className={classNames(bookingStyles.flowSection)}>
                                <PurchaseFlowHeader
                                    grsId={grsId}
                                    parkingMode={parkingMode}
                                    defaultCustomerFlow={defaultCustomerFlow}
                                    activeStep={activeStep}
                                    stepValues={bookingData}
                                    locationName={name}
                                    onStepClick={onStepClick}
                                    flowSteps={activeStepsFlow}
                                    stepPrefix={BOOKING_FLOW_HEADER_PREFIX}
                                    currentCustomerFlowType={currentCustomerFlow}
                                    onSwitchHourlyType={setCurrentCustomerFlow}
                                    showOnlyBookInAdvanceFlow={showOnlyBookInAdvanceFlow}
                                />
                                {isHourlyFlow ? (
                                    <HourlyFlow
                                        activeStep={activeStep}
                                        grsId={grsId}
                                        onNext={onNext}
                                        setStartDate={setStartDate}
                                        setProduct={setProduct}
                                        bookingData={bookingData}
                                        setVehicle={setVehicle}
                                        rate={rate}
                                        currentItemIndex={currentItemIndex}
                                        rateVehicle={rateVehicle}
                                        errorMessage={errorMessage}
                                        waitingListKey={waitingListKey}
                                        setOutboundNumber={setOutboundNumber}
                                        setInboundNumber={setInboundNumber}
                                        setAdditionalServices={setAdditionalServices}
                                        showPixPaymentMethod={showPixPaymentMethod}
                                        isParkNowFlow={isParkNowFlow}
                                        nonBookable={nonBookable}
                                        guestCheckoutEnabled={locationDetails.guestCheckoutEnabled}
                                        analyticsFlowType={analyticsFlowType}
                                    />
                                ) : (
                                    <EventFlow
                                        grsId={grsId}
                                        activeStep={activeStep}
                                        bookingData={bookingData}
                                        onNext={onNext}
                                        setDateRange={setDateRange}
                                        setEvent={setEvent}
                                        setVehicle={setVehicle}
                                        rateVehicle={rateVehicle}
                                        errorMessage={errorMessage}
                                        currentItemIndex={currentItemIndex}
                                        rate={rate}
                                        showPixPaymentMethod={showPixPaymentMethod}
                                        guestCheckoutEnabled={locationDetails.guestCheckoutEnabled}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
            {isDesktop() && showContent && (
                <div className={styles.sidebar}>
                    <ParkingInfoSideBar loading={detailsPending} location={locationDetails}>
                        {geoLocation && (
                            <Map geoLocation={geoLocation}>
                                <div style={MarkerStyle(30, 30)}>
                                    <Pin name={name} />
                                </div>
                            </Map>
                        )}
                    </ParkingInfoSideBar>
                </div>
            )}
        </div>
    )
}

export const BookingSelection = compose(
    connect(
        createStructuredSelector({
            ratePending: selectedTicketRatePendingSelector,
            credentialType: credentialTypeSelector,
            waitingListKey: waitingListKeySelector,
            rate: rateSelector,
            reservationAmendPolicy: cancellationSelector,
        }),
        {
            selectSeasonTicket,
            clearSelectedRate,
            setLastBookingDetails,
            removePromoCode,
            setRawPromoCode,
            clearGuestReservationsState,
            setCurrentCustomerFlow,
            setDateTimeRange,
        }
    )
)(withRates($BookingSelection))
